/**
 * useLibraryPluginFilter — URL state hook for the `/library` plugin filter
 * chip strip. Mirrors the contract of `useLibraryStatusFilter` but with the
 * id list supplied at runtime (plugins are tenant-scoped, not a static enum).
 *
 *   /library?plugins=marketing,operations  — multi-select via comma-list
 *   /library                                — implicit default (no filter, all visible)
 *
 * Contract:
 *  - Empty / missing `?plugins=` reads as `[]` (= "all" — no plugin filter).
 *  - Unknown ids silently stripped against the live `validPluginIds` set;
 *    URL is canonicalised via `setParams(next, { replace: true })`.
 *  - Setter writes `?plugins=` in canonical alphabetical-by-id order
 *    (matches `getAggregatedDocumentTypes` sort in `config-service.ts`).
 *  - When the new value is empty, the param is DELETED rather than rewritten —
 *    clean URLs at the default state.
 *  - Round-trips unrelated params (`?status=` etc.) untouched.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * URL param name (P10) — exported so consumers that manipulate the param
 * outside this hook (e.g. the page's atomic "Clear filters") can't drift
 * from the hook's contract.
 */
export const LIBRARY_PLUGIN_PARAM = 'plugins';

export interface LibraryPluginFilterState {
  pluginFilter: string[];
  setPluginFilter: (next: string[]) => void;
  isAllSelected: boolean;
}

function parsePluginsParam(
  raw: string | null,
  validIds: ReadonlySet<string>,
  canonicalOrder: readonly string[],
): string[] {
  if (!raw) return [];
  const parts = raw.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
  const set = new Set<string>();
  for (const p of parts) if (validIds.has(p)) set.add(p);
  return canonicalOrder.filter((id) => set.has(id));
}

function serialisePluginsParam(
  value: string[],
  canonicalOrder: readonly string[],
): string {
  const set = new Set<string>(value);
  return canonicalOrder.filter((id) => set.has(id)).join(',');
}

export function useLibraryPluginFilter(
  validPluginIds: readonly string[],
): LibraryPluginFilterState {
  const [params, setParams] = useSearchParams();
  const rawPlugins = params.get(LIBRARY_PLUGIN_PARAM);

  const canonicalOrder = useMemo(
    () => [...validPluginIds].sort((a, b) => a.localeCompare(b)),
    [validPluginIds],
  );
  const validSet = useMemo(() => new Set(validPluginIds), [validPluginIds]);

  const pluginFilter = useMemo<string[]>(
    () => parsePluginsParam(rawPlugins, validSet, canonicalOrder),
    [rawPlugins, validSet, canonicalOrder],
  );

  const canonical = useMemo(
    () => serialisePluginsParam(pluginFilter, canonicalOrder),
    [pluginFilter, canonicalOrder],
  );

  // Normalisation: strip unknown ids, canonicalise order, delete on empty.
  // Guarded on `validPluginIds.length > 0` so a deep-link arriving while
  // `usePluginConfig` is still loading is not destroyed (the load-phase
  // valid-set is empty, which would otherwise strip every id and rewrite
  // the URL before the real plugin list resolves).
  useEffect(() => {
    if (rawPlugins === null) return;
    if (validPluginIds.length === 0) return;
    if (pluginFilter.length === 0) {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(LIBRARY_PLUGIN_PARAM);
          return next;
        },
        { replace: true },
      );
    } else if (canonical !== rawPlugins) {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(LIBRARY_PLUGIN_PARAM, canonical);
          return next;
        },
        { replace: true },
      );
    }
  }, [rawPlugins, canonical, pluginFilter.length, validPluginIds.length, setParams]);

  const setPluginFilter = useCallback(
    (nextValue: string[]) => {
      setParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          const filtered = nextValue.filter((id) => validSet.has(id));
          if (filtered.length === 0) {
            updated.delete(LIBRARY_PLUGIN_PARAM);
          } else {
            updated.set(
              LIBRARY_PLUGIN_PARAM,
              serialisePluginsParam(filtered, canonicalOrder),
            );
          }
          return updated;
        },
        { replace: true },
      );
    },
    [setParams, validSet, canonicalOrder],
  );

  return {
    pluginFilter,
    setPluginFilter,
    isAllSelected: pluginFilter.length === 0,
  };
}

export default useLibraryPluginFilter;
